import { FunctionComponent, memo, useEffect, useState, createRef } from 'react';

// Contexts
import { ViewMedia, useViewDetected } from '@Components/Utils/ViewDetected';

// Components
import Share from '@Components/Utils/Anchor/Share';

import DoctorQuestion from '@Components/UI/DoctorQuestion';
import DownloadApp from '@Pages/bai-viet/Components/DownloadApp';
import Image from '@Components/Utils/Image';

// Libs
import { getHttpErrorMessage, useHttpContext } from '@Libs/http';

// API
import questionApi from '@Libs/api/user/question';
import { useAlertContext } from '@Components/Alert/Context';

// Analytics
import { trackingEvent } from '@Components/Utils/Lazy/Script';

// NPM
import Moment from 'moment';
import millify from 'millify';
import classnames from 'classnames';

// Styles
import styles from './AnswerDetail.module.scss';
import { getAttachmentAvatar } from '@Libs/utils';

type TAG = {
    id: string;
    slug: string;
    title: string;
    [key: string]: any;
};
interface AnswerDetailProps {
    content?: string;
    tags?: Array<TAG>;
    className?: string;
    questionComment: any;
    titleNewsFeed?: string;
    attachment?: { url: string };
}

const AnswerDetail: FunctionComponent<AnswerDetailProps> = memo(
    ({ tags, className, attachment, titleNewsFeed, content, questionComment }) => {
        const { alert } = useAlertContext();
        const { userHttp } = useHttpContext();
        const { deviceType } = useViewDetected();

        const isMobile = deviceType !== 'desktop';

        // useRef
        const tagsRef = createRef<HTMLDivElement>();

        // useState
        const [isCollapse, setIsCollapse] = useState(false);
        const [isThank, setIsThank] = useState<boolean>(true);
        const [countThank, setCountThank] = useState<number>(0);

        const toggle = () => setIsCollapse(!isCollapse);

        const wrapperClassName = classnames([styles.wrapper, { [styles.mobile]: isMobile }, className]);

        // Declare
        const { doctor, thanks, isThanked, id: questionCommentId, content: contentQuestion } = questionComment || {};
        const { id, profile } = doctor || {};

        const avatar = getAttachmentAvatar(id, 'doctor');
        const { average } = profile?.rating || 0;

        const { fullName: doctorName, graduateYear } = profile || {};
        const experience = Moment().year() - graduateYear;

        //useEffect
        useEffect(() => {
            if (tagsRef.current && isMobile) {
                if (!isCollapse) {
                    tagsRef.current.style.maxHeight = '72px';
                } else {
                    tagsRef.current.style.maxHeight = `${tagsRef.current.scrollHeight}px`;
                }
            }
        }, [isCollapse, isMobile]);

        useEffect(() => {
            setIsThank(isThanked);
            setCountThank(thanks?.length);
        }, [contentQuestion]);

        const handleClickThanks = (event) => {
            event.preventDefault();
            if (!isThanked) {
                const timer = setTimeout(async () => {
                    try {
                        await questionApi(userHttp).thanks(questionCommentId);
                        setIsThank(!isThank);
                        setCountThank((prevState) => prevState + 1);

                        // trackingEvent
                        trackingEvent('nhấp vào "Cảm ơn" khi đã đăng nhập', 'Cảm ơn');
                    } catch (error) {
                        alert({
                            type: 'error',
                            title: 'Đã xảy ra lỗi',
                            text: getHttpErrorMessage(error),
                        });

                        // trackingEvent
                        trackingEvent('nhấp vào "Cảm ơn" khi chưa đăng nhập', 'Cảm ơn');
                    } finally {
                        clearTimeout(timer);
                    }
                }, 300);
            } else {
                alert({
                    type: 'error',
                    title: 'Đã xảy ra lỗi',
                    text: 'Bạn đã cảm ơn bình luận này rồi',
                });
            }
        };

        // trackingEvent
        const handleClickShare = () => {
            trackingEvent('nhấp vào icon "Share"', 'Xem chi tiết câu hỏi');
        };

        const renderTitleImage = () => {
            return (
                <>
                    <div className={styles.detail__title}>{titleNewsFeed}</div>
                    <Image className={styles.detail__image} src={attachment?.url} width={'100%'} height={'auto'} />
                </>
            );
        };

        const renderTagDesktop = () => {
            return tags.map((tag) => (
                <span key={tag.id} className={styles.tag}>
                    {tag.title}
                </span>
            ));
        };

        const renderTagMobile = () => {
            const showReview = tags.slice(0, 5).map((tag) => {
                return (
                    <span key={tag.id} className={styles.tag}>
                        {tag.title}
                    </span>
                );
            });

            showReview.push(
                <ViewMedia is={['mobile', 'tablet']} key="icon_toggle">
                    <Image
                        width={28}
                        height={28}
                        onClick={toggle}
                        src="/assets/cong_dong_v2/icon_toggle.png"
                        className={classnames([styles.icon_more, { [styles.show]: !isCollapse }])}
                    />
                </ViewMedia>,
            );

            const showToggle = tags.slice(5, tags.length).map((tag) => {
                const tagClassName = classnames([styles.tag, { [styles.hide]: !isCollapse }]);

                return (
                    <span key={tag.id} className={tagClassName}>
                        {tag.title}
                    </span>
                );
            });

            return [...showReview, ...showToggle];
        };

        return (
            <div className={wrapperClassName}>
                {attachment && renderTitleImage()}

                <div className={classnames([styles.title, styles.title__question])}>Câu hỏi:</div>
                <div
                    className={classnames([styles.content, styles.content__question])}
                    dangerouslySetInnerHTML={{ __html: content }}
                />

                <div className={styles.title}>Trả lời:</div>

                <div className={styles.doctor_answer}>
                    <DoctorQuestion
                        className={styles.doctor}
                        {...{ avatar, average, doctorName, experience, id }}
                        isViewInfoDoctor
                    />
                    <ViewMedia is={'desktop'}>
                        <div className={styles.action_wrapper}>
                            <span
                                onClick={handleClickThanks}
                                className={classnames([
                                    styles.action,
                                    styles.thanks,
                                    {
                                        [styles.thanks__on]: isThank,
                                        [styles.thanks__off]: !isThank,
                                    },
                                ])}
                            >
                                <b>{millify(countThank || 0)}</b>
                                Cảm ơn
                            </span>
                            <Share>
                                <span className={classnames([styles.action, styles.share])}>Chia sẻ</span>
                            </Share>
                        </div>
                    </ViewMedia>
                </div>

                <div className={styles.content} dangerouslySetInnerHTML={{ __html: contentQuestion }} />

                <div ref={tagsRef} className={styles.tags}>
                    <span className={styles.tags_title}>Tags:</span>
                    {isMobile ? renderTagMobile() : renderTagDesktop()}
                    <ViewMedia is={['mobile', 'tablet']}>
                        <Image
                            width={28}
                            height={28}
                            onClick={toggle}
                            src="/assets/cong_dong_v2/icon_toggle.png"
                            className={classnames([styles.icon_less, { [styles.less]: isCollapse }])}
                        />
                    </ViewMedia>
                </div>

                <ViewMedia is={['mobile', 'tablet']}>
                    <div className={styles.action_wrapper}>
                        <span
                            onClick={handleClickThanks}
                            className={classnames([styles.action, styles.thanks], {
                                [styles.thanks__on]: isThank,
                                [styles.thanks__off]: !isThank,
                            })}
                        >
                            <b>{millify(countThank || 0)}</b>
                            Cảm ơn
                        </span>
                        <span className={classnames([styles.action, styles.share])} onClick={handleClickShare}>
                            <Share>Chia sẻ</Share>
                        </span>
                    </div>

                    <div className={styles.download_wrapper}>
                        <DownloadApp />
                    </div>
                </ViewMedia>
            </div>
        );
    },
);

AnswerDetail.defaultProps = {
    tags: [],
    className: '',
};

export default AnswerDetail;
